import React from 'react';
import { AddressMethod, AddressSearch, AddressSearchValue } from '@qbe/dfc-smart-ui';
import { AddressList } from '@qbe/dfc-services';
import { useLazyGetAddressesQuery, useStructuredAddressLookupMutation } from '../../store';
import type { AddressType } from './addressTypes';
import { ADDRESS_FIELD_MAPS } from './addressHelpers';
import { useAddressFieldOverrides } from '../../hooks/useAddressFieldOverrides';
import { Box } from '@qbe/ads-core';

interface AddressDefaults {
  addressObject: AddressList | null;
  defaultManualAddress: {
    streetAddress1: string;
    city: string;
    state: string;
    postcode: string;
  } | null;
}

export interface CmsAddressSearchProps {
  /** Which address block to read/write in the form */
  type: AddressType;
  /** Called when address lookup/manual values change */
  onAddressChange: (
    addressList: AddressList | null,
    structured: AddressSearchValue | null,
    fieldName: string,
    method: AddressMethod
  ) => boolean;
  /** Returns current values for AddressSearch default hydration */
  getAddressDefaults: () => AddressDefaults;
  required?: boolean;
}

const CmsAddressSearch = ({ type, onAddressChange, getAddressDefaults, required = false }: CmsAddressSearchProps) => {
  // Key is incremented to force a remount when switching from lookup to manual so that
  // the library re-reads `defaultManualAddress` and pre-fills its internal form state.
  const [addressSearchKey, setAddressSearchKey] = React.useState(0);

  const fieldOverrides = useAddressFieldOverrides(type);
  const handleAddressChange = React.useCallback(
    (
      addressList: AddressList | null,
      structured: AddressSearchValue | null,
      fieldName: string,
      method: AddressMethod
    ) => {
      const shouldRemount = onAddressChange(addressList, structured, fieldName, method);
      if (shouldRemount) {
        setAddressSearchKey((k) => k + 1);
      }
    },
    [onAddressChange]
  );

  const handleAddressesError = () => {
    console.error('Address lookup service error occurred');
  };

  // Computed each render so the remounted <AddressSearch> picks up updated form values.
  const { addressObject, defaultManualAddress } = getAddressDefaults();

  return (
    <Box maxWidth={400} width="100%">
      <AddressSearch
        key={addressSearchKey}
        validatePostcodeState
        fieldName={ADDRESS_FIELD_MAPS[type].fieldName}
        fieldOverrides={fieldOverrides}
        defaultAddress={(addressObject as AddressList) ?? undefined}
        defaultManualAddress={defaultManualAddress}
        addressApiHooks={{
          useLazyGetAddressesQuery,
          useStructuredAddressLookupMutation,
        }}
        onChange={handleAddressChange}
        onAddressesError={handleAddressesError}
        required={required}
        showManualCountry
      />
    </Box>
  );
};

export default CmsAddressSearch;
